import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { getSdk } from '@/lib/sdk';
import type { Ulid } from '@/types';
import { feedKeys } from './useFeed';

export const postKeys = {
  all: ['posts'] as const,
  detail: (ulid: Ulid) => ['posts', ulid] as const,
  batch: (ulids: Ulid[]) => ['posts', 'batch', ulids] as const,
  comments: (ulid: Ulid) => ['posts', ulid, 'comments'] as const,
};

/**
 * Fetch a single post by ULID.
 */
export function usePost(ulid: Ulid | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: postKeys.detail(ulid ?? ''),
    queryFn: () => sdk.getPostByUlid(ulid!),
    enabled: Boolean(ulid),
  });
}

/**
 * Batch-fetch multiple posts by ULID.
 */
export function usePostsBatch(ulids: Ulid[]) {
  const sdk = getSdk();

  return useQuery({
    queryKey: postKeys.batch(ulids),
    queryFn: () => sdk.fetchPostsBatch(ulids),
    enabled: ulids.length > 0,
  });
}

/**
 * Paginated comments for a post.
 */
export function useComments(postUlid: Ulid | undefined) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: postKeys.comments(postUlid ?? ''),
    queryFn: async ({ pageParam }) => {
      return sdk.fetchComments({
        ulid: postUlid!,
        cursor: pageParam,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(postUlid),
  });
}

/**
 * Like/unlike a post (toggles 'heart' reaction).
 * Component should handle optimistic UI state locally.
 */
export function useLikePost() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ulid, liked }: { ulid: Ulid; liked: boolean }) => {
      if (liked) {
        await sdk.removeReaction(ulid, 'heart');
      } else {
        await sdk.addReaction(ulid, 'heart');
      }
      return !liked;
    },
    onSettled: async (_data, _error, { ulid }) => {
      // Clear SDK cache and refresh to ensure persistence across reloads
      await sdk.clearCache();
      await sdk.getPostByUlid(ulid, true);
      queryClient.invalidateQueries({ queryKey: postKeys.detail(ulid) });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
    },
  });
}

/**
 * Bookmark/unbookmark a post.
 */
export function useBookmarkPost() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ulid, bookmarked }: { ulid: Ulid; bookmarked: boolean }) => {
      if (bookmarked) {
        await sdk.unbookmarkPost(ulid);
      } else {
        await sdk.bookmarkPost(ulid);
      }
      return !bookmarked;
    },
    onSettled: async (_data, _error, { ulid }) => {
      // Clear SDK cache and refresh to ensure persistence across reloads
      await sdk.clearCache();
      await sdk.getPostByUlid(ulid, true);
      queryClient.invalidateQueries({ queryKey: postKeys.detail(ulid) });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
    },
  });
}

/**
 * Create a comment on a post.
 */
export function useCreateComment() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      postUlid,
      body,
      parentId,
    }: {
      postUlid: Ulid;
      body: string;
      /** Parent comment ULID when replying to a comment (omit for root comments) */
      parentId?: Ulid;
    }) => {
      // If parentId is provided, we're replying to a comment; otherwise to the post
      return sdk.createComment({ parentId: parentId ?? postUlid, body });
    },
    onSuccess: (_data, { postUlid }) => {
      queryClient.invalidateQueries({ queryKey: postKeys.comments(postUlid) });
      queryClient.invalidateQueries({ queryKey: postKeys.detail(postUlid) });
    },
  });
}

/**
 * Add a reaction to a post.
 */
export function useReaction() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ulid, reaction, active }: { ulid: Ulid; reaction: string; active: boolean }) => {
      if (active) {
        await sdk.removeReaction(ulid, reaction);
      } else {
        await sdk.addReaction(ulid, reaction);
      }
    },
    onSuccess: (_data, { ulid }) => {
      queryClient.invalidateQueries({ queryKey: postKeys.detail(ulid) });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
    },
  });
}

/**
 * Repost a post.
 */
export function useRepost() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ulid, reposted }: { ulid: Ulid; reposted: boolean }) => {
      if (reposted) {
        await sdk.unrepost(ulid);
      } else {
        await sdk.repost(ulid);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
    },
  });
}

/**
 * Create a new post.
 */
export function useCreatePost() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { groupUlid, ...rest } = payload;
      const groupPayload = groupUlid ? { groupId: groupUlid } : { groupName: 'default' };
      return sdk.createPost({
        ...groupPayload,
        ...rest,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      if (variables.groupUlid) {
        queryClient.invalidateQueries({ queryKey: ['groups', variables.groupUlid, 'posts'] });
      }
    },
  });
}

/**
 * Delete a post.
 */
export function useDeletePost() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ulid: Ulid) => {
      await sdk.deletePost(ulid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
    },
  });
}

/**
 * Vote on a poll.
 */
export function useVotePoll() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ulid, optionId }: { ulid: Ulid; optionId: number }) => {
      return sdk.votePoll(ulid, optionId);
    },
    onSuccess: (_data, { ulid }) => {
      queryClient.invalidateQueries({ queryKey: postKeys.detail(ulid) });
    },
  });
}
